/**
 * Course Config Cache
 * Higher-level wrapper around generated CoursesCacheStorage.
 * Handles the shape transform from generated wire types to internal
 * domain types (LanguageCourse, LocaleMetadataFull, CountryLocaleConfig).
 *
 * Freshness is owned by `freshnessManager` (see services/freshnessManager.ts).
 * Ask `freshnessManager.isFresh('courses')` to decide whether to skip an
 * API roundtrip on cold start — this module no longer tracks its own
 * `fetchedAt` timestamp because that would duplicate the framework's
 * persisted `lastSyncedAt` state.
 */

import { CoursesCacheStorage } from '../generated/sync/sync-config';
import { createLogger } from '../utils/logger';
import type { LanguageCourse, LocaleMetadataFull } from '../types/locale';
import type { CountryLocaleConfig } from '../data/countryConfig';

const log = createLogger('CourseCache');

export interface CachedCourseConfig {
  courses: LanguageCourse[];
  locales: LocaleMetadataFull[];
  countries: CountryLocaleConfig[];
}

/**
 * Load cached course config from AsyncStorage.
 * Returns null if no cache exists or if it's corrupted.
 * Version validation and Zod schema checking are handled by the generated accessor.
 */
export async function loadCourseCache(): Promise<CachedCourseConfig | null> {
  try {
    const raw = await CoursesCacheStorage.load();
    if (!raw) return null;

    return {
      courses: raw.courses as LanguageCourse[],
      locales: raw.locales as LocaleMetadataFull[],
      countries: raw.countries as CountryLocaleConfig[],
    };
  } catch (error) {
    log.warn('Failed to load course cache', { error });
    return null;
  }
}

/**
 * Clear course cache (e.g., on logout or data reset).
 */
export async function clearCourseCache(): Promise<void> {
  try {
    await CoursesCacheStorage.clear();
  } catch (error) {
    log.warn('Failed to clear course cache', { error });
  }
}
